import { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { BookmarkIconButton } from '@bonusly/common-bookmark';
import { RelativeTime } from '@bonusly/common-relative-time';
import { Box, Display, HStack, Image, rem, Spacer, useBreakpointValue } from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { AwardType } from '../../../../../types/bonus';
import { Reveal } from '../../../../display/reveal';
import { BonusOptionsMenu, BonusOptionsMenuProps } from '../../components/bonus/bonus-options-menu';
import { Chip } from '../../components/common/chip';
import { UserAvatarGroup } from '../../components/user-avatar';
import { Route } from '../../config/router';
import { useAwardIcon } from '../../hooks/use-award-icon';
import { BonusFeedTrackedAction, useTrackBonusFeedItemEvent } from '../../hooks/use-bonus-feed-item-analytics';
import { pathWithId } from '../../utils/api';

export type AwardHeaderProps = BonusOptionsMenuProps & {
	displayOptionsMenu?: boolean;
	displayAmount?: boolean;
	variant?: 'default' | 'collapsed';
};

export const AwardHeader: FC<AwardHeaderProps> = ({ bonus, hideBookmarkButton, variant = 'default', ...rest }) => {
	const recognizeURL = useBuildRecognizeURL();
	const max = useBreakpointValue({ base: 1, sm: 3 });
	const { t } = useTranslation('recognition');
	const company = useCurrentCompany();
	const trackAvatarClick = useTrackBonusFeedItemEvent(bonus, BonusFeedTrackedAction.AvatarClick);
	const awardRenameLabel = bonus.award_type === AwardType.Claimable ? t('awards.types.incentives') : null;

	const displayAmount = variant === 'default';
	const displayOptionsMenu = variant === 'default';
	const displaySubHeader = variant === 'default';

	const showAmount = displayAmount && company?.show_bonus_amounts && !!bonus.family_amount;

	return (
		<Box data-testid="award-header">
			<HStack alignItems="center" spacing={2}>
				<Image
					h={10}
					w={10}
					aria-label={awardRenameLabel ?? t(`awards.types.${bonus.award_type || 'award'}`) ?? ''}
					src={useAwardIcon(bonus)}
					borderRadius="full"
					objectFit="cover"
				/>

				<Reveal if={showAmount}>
					<Chip size="sm" variant="amount" height="auto">
						+{bonus.family_amount}
					</Chip>
				</Reveal>

				<Spacer />

				<RelativeTime color="text.soft" date={new Date(bonus.created_at)} />

				{!hideBookmarkButton && <BookmarkIconButton />}

				{displayOptionsMenu && <BonusOptionsMenu bonus={bonus} boxSize={10} {...rest} />}
			</HStack>

			{displaySubHeader && (
				<Box display={{ base: 'block', '2xs': 'flex' }} dir="row" alignItems="center" mt={2}>
					<Display
						as="a"
						fontSize={rem(32)}
						fontWeight="bold"
						href={recognizeURL(pathWithId(Route.UserProfile, bonus.giver.id))}
						lineHeight="short"
						onClick={(event) => event.stopPropagation()}
						pr={{ base: 0, xs: 6 }}
					>
						{bonus.giver.display_name}
					</Display>
					<Spacer />
					<UserAvatarGroup
						max={max}
						mr={rem(64 / 2.75)}
						size="xl"
						users={bonus.receivers}
						recipients={bonus.recipients}
						onAvatarClick={(event) => {
							trackAvatarClick();
							event.stopPropagation();
						}}
					/>
				</Box>
			)}
		</Box>
	);
};
